import React from 'react';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';

/**
 * MathText component that renders text with LaTeX mathematical formulas
 * Supports both inline formulas (wrapped in $...$) and block formulas (wrapped in $$...$$)
 * 
 * Example usage:
 * <MathText text="The formula is $D = \frac{1}{2}gt^2$ for distance." />
 * <MathText text="Block formula: $$D = \frac{1}{2}gt^2$$" />
 */
const MathText = ({ text, className = '' }) => {
  if (!text) return null;

  // Split text by block math ($$...$$) and inline math ($...$)
  const renderTextWithMath = (inputText) => {
    const parts = [];
    let currentIndex = 0;
    let key = 0;

    // First, handle block math ($$...$$)
    const blockMathRegex = /\$\$(.*?)\$\$/gs;
    let blockMatch;
    const blockMathPositions = [];

    while ((blockMatch = blockMathRegex.exec(inputText)) !== null) {
      blockMathPositions.push({
        start: blockMatch.index,
        end: blockMatch.index + blockMatch[0].length,
        formula: blockMatch[1].trim()
      });
    }

    // Process text with both block and inline math
    let remainingText = inputText;
    let offset = 0;

    blockMathPositions.forEach(block => {
      // Add text before block math
      const beforeText = remainingText.substring(0, block.start - offset);
      if (beforeText) {
        parts.push(...processInlineMath(beforeText, key));
        key += 100;
      }

      // Add block math
      parts.push(
        <div key={`block-${key++}`} className="my-3">
          <BlockMath math={block.formula} />
        </div>
      );

      // Update remaining text
      remainingText = remainingText.substring(block.end - offset);
      offset = block.end;
    });

    // Process remaining text for inline math
    if (remainingText) {
      parts.push(...processInlineMath(remainingText, key));
    }

    return parts;
  };

  const processInlineMath = (text, startKey) => {
    const parts = [];
    const inlineMathRegex = /\$(.*?)\$/g;
    let lastIndex = 0;
    let match;
    let key = startKey;

    while ((match = inlineMathRegex.exec(text)) !== null) {
      // Add text before the math
      if (match.index > lastIndex) {
        const textBefore = text.substring(lastIndex, match.index);
        parts.push(<span key={`text-${key++}`}>{textBefore}</span>);
      }

      // Add the inline math
      const formula = match[1].trim();
      if (formula) {
        try {
          parts.push(<InlineMath key={`math-${key++}`} math={formula} />);
        } catch (error) {
          // If LaTeX parsing fails, show the original text
          parts.push(<span key={`error-${key++}`}>${formula}$</span>);
        }
      }

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(<span key={`text-${key++}`}>{text.substring(lastIndex)}</span>);
    }

    return parts;
  };

  return (
    <div className={`math-text ${className}`}>
      {renderTextWithMath(text)}
    </div>
  );
};

export default MathText;
